import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import LanguagePicker from "../components/LanguagePicker";
import Navbar from "../components/Navbar";
import { useI18n } from "../context/I18nContext";
import { Globe, Loader2 } from "lucide-react";

export default function Auth({ mode }) {
  const isRegister = mode === "register";
  const { appUserLogin, appUserRegister } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "", country: "" });
  const [native, setNative] = useState([]);
  const [learning, setLearning] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => { const v = e.target.value; setForm((f) => ({ ...f, [k]: v })); };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (isRegister) {
        await appUserRegister({ ...form, native_languages: native, learning_languages: learning });
      } else {
        await appUserLogin(form.email, form.password);
      }
      nav("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-md mx-auto px-5 py-12">
        <div className="rounded-3xl bg-card border border-border shadow-sm p-8">
          <span className="w-12 h-12 rounded-2xl bg-primary flex items-center justify-center text-primary-foreground mb-5">
            <Globe className="w-6 h-6" />
          </span>
          <h1 className="text-2xl sm:text-3xl font-heading font-black tracking-tight">
            {isRegister ? t("auth.createTitle") : t("auth.welcomeTitle")}
          </h1>
          <p className="text-foreground/70 mt-1">
            {isRegister ? t("auth.createSub") : t("auth.welcomeSub")}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="auth-form">
            {isRegister && (
              <div className="space-y-2">
                <Label>{t("auth.name")}</Label>
                <Input required value={form.name} onChange={set("name")} placeholder="Jane Doe"
                  className="rounded-2xl bg-accent/50 border-transparent h-11" data-testid="input-name" />
              </div>
            )}
            <div className="space-y-2">
              <Label>{t("auth.email")}</Label>
              <Input required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com"
                className="rounded-2xl bg-accent/50 border-transparent h-11" data-testid="input-email" />
            </div>
            <div className="space-y-2">
              <Label>{t("auth.password")}</Label>
              <Input required type="password" value={form.password} onChange={set("password")} placeholder="••••••••"
                className="rounded-2xl bg-accent/50 border-transparent h-11" data-testid="input-password" />
            </div>
            {isRegister && (
              <>
                <div className="space-y-2">
                  <Label>{t("auth.country")}</Label>
                  <Input value={form.country} onChange={set("country")} placeholder="Spain"
                    className="rounded-2xl bg-accent/50 border-transparent h-11" data-testid="input-country" />
                </div>
                <LanguagePicker label={t("auth.iSpeak")} selected={native} onChange={setNative} color="secondary" testid="native-picker" />
                <LanguagePicker label={t("auth.imLearning")} selected={learning} onChange={setLearning} color="primary" testid="learning-picker" />
              </>
            )}

            {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}

            <Button type="submit" disabled={loading} className="w-full rounded-full h-11 text-base" data-testid="auth-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isRegister ? t("auth.createBtn") : t("auth.loginBtn")}
            </Button>
          </form>

          <p className="text-sm text-center text-foreground/70 mt-5">
            {isRegister ? `${t("auth.haveAccount")} ` : `${t("auth.newHere")} `}
            <Link to={isRegister ? "/login" : "/register"} className="text-primary font-semibold" data-testid="auth-switch">
              {isRegister ? t("auth.switchLogin") : t("auth.switchSignup")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
